/*
 * @Description: 实时定位驾驶舱 — 顶部刷新控件
 *   左：立即刷新按钮；右：自动刷新频率下拉（10秒/30秒/1分钟/自定义）。
 *   自定义范围 10 秒 ~ 1 小时；选中后页面按该频率轮询刷新。
 */
import React, { useEffect, useRef, useState } from 'react'
import { Dropdown, InputNumber, Modal, message } from 'antd'
import { DownOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  CUSTOM_REFRESH_MAX_SEC,
  CUSTOM_REFRESH_MIN_SEC,
  REFRESH_INTERVAL_OPTIONS
} from '../../constants'
import './index.scss'

interface RefreshControlProps {
  /** 当前刷新间隔（毫秒） */
  value: number
  /** 刷新间隔变化 */
  onChange: (ms: number) => void
  /** 立即刷新一次 */
  onRefresh: () => void
}

/** 毫秒 → 简短中文（10秒 / 1分钟 / 2分30秒） */
function formatInterval(ms: number): string {
  const preset = REFRESH_INTERVAL_OPTIONS.find((o) => o.value === ms)
  if (preset) return preset.label
  const sec = Math.round(ms / 1000)
  if (sec < 60) return `${sec}秒`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return s === 0 ? `${m}分钟` : `${m}分${s}秒`
}

const RefreshControl: React.FC<RefreshControlProps> = ({
  value,
  onChange,
  onRefresh
}) => {
  const [customOpen, setCustomOpen] = useState(false)
  const [customSec, setCustomSec] = useState<number>(Math.round(value / 1000))
  // 立即刷新：图标旋转动画 + 成功提示
  const [refreshing, setRefreshing] = useState(false)
  const spinTimerRef = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(spinTimerRef.current), [])

  const handleRefresh = () => {
    if (refreshing) return
    setRefreshing(true)
    onRefresh()
    message.success('刷新成功')
    // 旋转约 0.8s 给出"正在刷新"的反馈
    spinTimerRef.current = window.setTimeout(() => setRefreshing(false), 800)
  }

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'custom') {
      setCustomSec(Math.round(value / 1000))
      setCustomOpen(true)
      return
    }
    onChange(Number(key))
  }

  const handleCustomOk = () => {
    const sec = Math.max(
      CUSTOM_REFRESH_MIN_SEC,
      Math.min(CUSTOM_REFRESH_MAX_SEC, Math.round(customSec || 0))
    )
    onChange(sec * 1000)
    setCustomOpen(false)
  }

  const menu = {
    selectedKeys: [String(value)],
    onClick: handleMenuClick,
    items: [
      ...REFRESH_INTERVAL_OPTIONS.map((o) => ({
        key: String(o.value),
        label: o.label
      })),
      { key: 'custom', label: '自定义…' }
    ]
  }

  return (
    <div className="cockpit-refresh">
      <button
        type="button"
        className="cockpit-refresh__btn"
        onClick={handleRefresh}
        title="立即刷新"
      >
        <ReloadOutlined spin={refreshing} />
      </button>
      <Dropdown menu={menu} trigger={['click']}>
        <span className="cockpit-refresh__select">
          {formatInterval(value)} 自动 <DownOutlined />
        </span>
      </Dropdown>

      <Modal
        title="自定义刷新频率"
        open={customOpen}
        width={380}
        destroyOnHidden
        onOk={handleCustomOk}
        onCancel={() => setCustomOpen(false)}
      >
        <div className="cockpit-refresh__custom">
          每
          <InputNumber
            min={CUSTOM_REFRESH_MIN_SEC}
            max={CUSTOM_REFRESH_MAX_SEC}
            value={customSec}
            onChange={(v) =>
              setCustomSec(typeof v === 'number' ? v : Number(v) || 0)
            }
            className="cockpit-refresh__input"
          />
          秒刷新一次
        </div>
        <div className="cockpit-refresh__hint">
          范围 {CUSTOM_REFRESH_MIN_SEC} 秒 ~ {CUSTOM_REFRESH_MAX_SEC / 60} 分钟
        </div>
      </Modal>
    </div>
  )
}

export default RefreshControl
